import React, { useEffect, useRef, useState } from 'react';
import './hue-slider.scss';
import { Oscilloscope } from './oscilloscope';
import enData from '../../data/hue-color-content-enus.json';

const mod = (n: number, m: number) => ((n % m) + m) % m;

const HUE_KEY = 'hue-slider';

const displayHue = (raw: number): number => ((raw % 360) + 360) % 360;

const roundHue = (raw: number): number =>
  ((Math.round(raw) % 360) + 360) % 360;

const angularDist = (a: number, b: number): number => {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
};

const SHOW_RANGE = 15;

const degreeRound = (hv: number): number =>
  Math.round((((hv % 360) + 360) % 360) / 20) * 20;

const HUE_MARKS = new Set<number>(enData.map((e) => degreeRound(e['hue-value'])));

const timeHue = (): number => {
  const now = new Date();
  const minutes = now.getHours() * 60 + now.getMinutes();
  return Math.round(mod((minutes / 1440) * 360, 360));
};

const loadHue = (): number => {
  try {
    const raw = localStorage.getItem(HUE_KEY);
    if (raw === null) return timeHue();
    const value = Number(raw);
    if (Number.isNaN(value)) return timeHue();
    return Math.round(mod(value, 360));
  } catch {
    return timeHue();
  }
};

export interface HueSliderProps {
  variant?: 'linear' | 'circular';
  onActiveChange?: (active: boolean) => void;
  onHueChange?: (hue: number) => void;
}

export const HueSlider: React.FC<HueSliderProps> = ({
  variant = 'linear',
  onActiveChange,
  onHueChange,
}) => {
  const [initialHue] = useState<number>(loadHue);
  const containerRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const knobRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const startPointerRef = useRef(0);
  const startDragRef = useRef(0);
  const startAngleRef = useRef(0);
  const startValueRef = useRef(0);
  const dragRef = useRef(0);
  const angleRef = useRef(mod(-initialHue, 360));
  const widthRef = useRef(0);
  const rafRef = useRef<number>(0);
  const lastHueRef = useRef(0);
  const lastPersistedRef = useRef(0);
  const onHueChangeRef = useRef(onHueChange);
  const hueRef = useRef(initialHue);
  const [hue, setHue] = useState(initialHue);
  const [angle, setAngle] = useState(0);
  const [width, setWidth] = useState(0);

  const isCircular = variant === 'circular';

  const setDragFromLinear = (nextDrag: number) => {
    const w = widthRef.current > 0 ? widthRef.current : 1;
    const period = w * 4;
    const halfPeriod = period / 2;
    const wrapped =
      ((((nextDrag + halfPeriod) % period) + period) % period) - halfPeriod;
    dragRef.current = wrapped;
    hueRef.current = mod(initialHue - (wrapped / period) * 360, 360);
  };

  const setAngleFrom = (nextAngle: number) => {
    angleRef.current = mod(nextAngle, 360);
    hueRef.current = mod(-nextAngle, 360);
  };

  const paint = () => {
    if (isCircular) {
      if (ringRef.current) {
        ringRef.current.style.transform = `rotate(${angleRef.current}deg)`;
      }
    } else if (barRef.current) {
      barRef.current.style.transform = `translateX(calc(-50% + ${dragRef.current}px))`;
    }
    if (knobRef.current) {
      knobRef.current.style.background = `hsl(${hueRef.current} 100% 60%)`;
    }
    if (containerRef.current) {
      containerRef.current.setAttribute(
        'aria-valuenow',
        String(displayHue(Math.round(hueRef.current))),
      );
    }
  };

  const applyTheme = () => {
    const rounded = roundHue(hueRef.current);
    if (lastHueRef.current === rounded) return;
    lastHueRef.current = rounded;
    document.documentElement.style.setProperty('--hue', String(rounded));
  };

  const persistHue = () => {
    const rounded = roundHue(hueRef.current);
    if (lastPersistedRef.current === rounded) return;
    lastPersistedRef.current = rounded;
    try {
      localStorage.setItem(HUE_KEY, String(rounded));
    } catch {
      // ignore storage errors
    }
  };

  const scheduleTheme = () => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      applyTheme();
      persistHue();
      const rounded = roundHue(hueRef.current);
      onHueChangeRef.current?.(rounded);
      setHue((prev) => (prev === rounded ? prev : rounded));
      const roundedAngle = Math.round(angleRef.current);
      setAngle((prev) => (prev === roundedAngle ? prev : roundedAngle));
    });
  };

  const dragLinear = (nextDrag: number) => {
    setDragFromLinear(nextDrag);
    paint();
    scheduleTheme();
  };

  const dragAngle = (nextAngle: number) => {
    setAngleFrom(nextAngle);
    paint();
    scheduleTheme();
  };

  useEffect(() => {
    onHueChangeRef.current = onHueChange;
  }, [onHueChange]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--hue',
      String(roundHue(initialHue)),
    );
    lastHueRef.current = roundHue(initialHue);
    lastPersistedRef.current = roundHue(initialHue);
    setHue(roundHue(initialHue));
    onHueChangeRef.current?.(roundHue(initialHue));
    setAngle(Math.round(mod(-initialHue, 360)));
    const el = containerRef.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      observer.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [initialHue]);

  useEffect(() => {
    widthRef.current = width;
    if (width <= 0) return;
    if (isCircular) {
      setAngleFrom(angleRef.current);
      paint();
      return;
    }
    const period = width * 4;
    const halfPeriod = period / 2;
    const nextDrag = mod(((initialHue - hueRef.current) / 360) * period, period);
    dragRef.current = nextDrag > halfPeriod ? nextDrag - period : nextDrag;
    hueRef.current = mod(initialHue - (dragRef.current / period) * 360, 360);
    paint();
  }, [width, initialHue]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    draggingRef.current = true;
    if (isCircular) {
      const center = getCenter();
      startAngleRef.current =
        (Math.atan2(e.clientY - center.y, e.clientX - center.x) * 180) / Math.PI;
      startValueRef.current = angleRef.current;
    } else {
      startPointerRef.current = e.clientX;
      startDragRef.current = dragRef.current;
    }
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    if (isCircular) {
      const center = getCenter();
      const angle =
        (Math.atan2(e.clientY - center.y, e.clientX - center.x) * 180) /
        Math.PI;
      const delta = angle - startAngleRef.current;
      dragAngle(startValueRef.current + delta);
    } else {
      dragLinear(
        startDragRef.current + (e.clientX - startPointerRef.current),
      );
    }
  };

  const getCenter = () => {
    const el = containerRef.current;
    const rect = el?.getBoundingClientRect();
    return {
      x: (rect?.left ?? 0) + (rect?.width ?? 0) / 2,
      y: (rect?.top ?? 0) + (rect?.height ?? 0) / 2,
    };
  };

  const handlePointerUp = () => {
    draggingRef.current = false;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (isCircular) {
      const step = 15;
      if (e.key === 'ArrowLeft') dragAngle(angleRef.current - step);
      else if (e.key === 'ArrowRight') dragAngle(angleRef.current + step);
      else if (e.key === 'Home') dragAngle(0);
      else if (e.key === 'End') dragAngle(359);
      return;
    }
    const w = widthRef.current > 0 ? widthRef.current : 1;
    const step = (15 / 360) * w * 4;
    if (e.key === 'ArrowLeft') dragLinear(dragRef.current - step);
    else if (e.key === 'ArrowRight') dragLinear(dragRef.current + step);
    else if (e.key === 'Home') dragLinear(0);
    else if (e.key === 'End') dragLinear(w * 2);
  };

  const barStyle: React.CSSProperties =
    width > 0
      ? {
          width: width * 5,
          left: '50%',
          transform: 'translateX(-50%)',
          background: `repeating-linear-gradient(90deg, ${Array.from(
            { length: 7 },
            (_, i) =>
              `hsl(${initialHue - 225 + i * 60} 100% 60%) ${
                (i * width * 4) / 6
              }px`,
          ).join(', ')})`,
        }
      : { width: '200%', transform: 'translateX(-50%)' };

  const ringGradient = `conic-gradient(${Array.from(
    { length: 36 },
    (_, i) => `hsl(${i * 10} 100% 60%) ${i * 10}deg`,
  ).join(', ')})`;

  const knobStyle: React.CSSProperties = {
    background: `hsl(${hueRef.current} 100% 60%)`,
  };

  const activeDegree = (() => {
    let best = enData[0];
    let bestD = 360;
    for (const e of enData) {
      const d = angularDist(hue, e['hue-value']);
      if (d < bestD) {
        bestD = d;
        best = e;
      }
    }
    if (bestD > SHOW_RANGE) return -1;
    return degreeRound(best['hue-value']);
  })();

  const containerClass = isCircular
    ? 'hue-slider hue-slider--circular'
    : 'hue-slider';

  return (
    <div
      ref={containerRef}
      className={containerClass}
      role="slider"
      aria-label="Hue"
      aria-orientation={isCircular ? undefined : 'horizontal'}
      aria-valuemin={0}
      aria-valuemax={359}
      aria-valuenow={displayHue(Math.round(hueRef.current))}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onLostPointerCapture={handlePointerUp}
      onPointerEnter={() => onActiveChange?.(true)}
      onPointerLeave={() => onActiveChange?.(false)}
      onKeyDown={handleKeyDown}
    >
      {isCircular ? (
        <>
          <div
            ref={ringRef}
            className="hue-slider__ring"
            style={{ background: ringGradient }}
            aria-hidden="true"
          />
          <div className="hue-slider__ruler" aria-hidden="true">
            {Array.from({ length: 18 }, (_, i) => {
              const d = i * 20;
              const rot = d + angle;
              const r = width > 0 ? width / 2 + 20 : 0;
              const classes = [
                'hue-slider__degree',
                d === 0 ? 'first-degree' : '',
                HUE_MARKS.has(d) ? 'has-mark' : '',
                d === activeDegree ? 'is-current' : '',
              ].filter(Boolean).join(' ');
              return (
                <span
                  key={d}
                  className={classes}
                  style={{
                    transform: `translate(-50%, -50%) rotate(${rot}deg) translateY(${-r}px)`,
                  }}
                >
                  {d === 360 ? 0 : d}
                  {HUE_MARKS.has(d) && <span className="hue-slider__mark" />}
                </span>
              );
            })}
          </div>
          <div className="hue-slider__scope" aria-hidden="true">
            <Oscilloscope hue={hue} compact crt variant='wave' />
          </div>
          <div ref={knobRef} className="hue-slider__knob" style={knobStyle}>
            <span className="hue-slider__knob-value">
              {String(displayHue(Math.round(hue)))}
            </span>
          </div>
        </>
      ) : (
        <>
          <div
            ref={barRef}
            className="hue-slider__bar"
            style={barStyle}
            aria-hidden="true"
          />
          <div className="hue-slider__scope" aria-hidden="true">
            <Oscilloscope hue={hue} compact variant='wave' crt />
          </div>
          <div
            ref={knobRef}
            className="hue-slider__knob"
            style={knobStyle}
            aria-hidden="true"
          />
        </>
      )}
    </div>
  );
};